import { warn } from '../core'

/** 历史栈 */
class HistoryStack<T> {

  /** 历史栈最大可以存储多少数据 */
  #stackMaxSize: number
  /** 双向链表 */
  #linkedList: PointerDoubleLinkedList<T>

  /**
   * @param firstNode       初始化的item
   * @param stackMaxSize    历史栈最大可以存储多少数据
   */
  constructor(firstNode: T, stackMaxSize = 20) {
    if (stackMaxSize < 1 && stackMaxSize > 100) throw new Error('stackMaxSize只能在1-100区间内')
    this.#stackMaxSize = stackMaxSize
    this.#linkedList = new PointerDoubleLinkedList(firstNode)
  }

  /** 进栈 */
  pushStack(item: T) {
    this.#linkedList.append(item)
    if (this.#linkedList.length > this.#stackMaxSize) {
      this.#linkedList.removeHead()
    }
  }

  /** 更新栈（某些情况下不需要更新栈，比如表单返回的格式不合法，需要本地修正） */
  updatePointer(item: T) {
    this.#linkedList.updatePointer(item)
  }

  /** 能否撤销 */
  canUndo() {
    return this.#linkedList.isPrev()
  }

  /** 能否重做 */
  canRedo() {
    return this.#linkedList.isNext()
  }

  /** 撤销 */
  undo() {
    try {
      return this.#linkedList.prev(), this.getCurrent()
    } catch (e) {
      warn('undo fail', e)
    }
  }

  /** 重做 */
  redo() {
    try {
      return this.#linkedList.next(), this.getCurrent()
    } catch (e) {
      warn('redo fail', e)
    }
  }

  /** 获取当前项 */
  getCurrent = () => this.#linkedList.getPointerNode()

  /** 获取历史栈的大小 */
  getStackSize = () => this.#linkedList.length

}

/** 链接节点 */
class LinkedNode<T> {

  /** 上一个节点 */
  prev: LinkedNode<T> | null = null
  /** 当前节点存储的数据 */
  item: T
  /** 下一个节点 */
  next: LinkedNode<T> | null = null

  constructor(item: T) {
    this.item = item
  }

}

/** 双向链表 */
class PointerDoubleLinkedList<T> {

  /** 头节点 */
  #head!: LinkedNode<T>
  /** 末尾节点 */
  #tail!: LinkedNode<T>
  /** 当前指向的node */
  #pointerNode!: LinkedNode<T>
  /** 链表长度 */
  length = 0

  /**
   * @param firstNode  第一个节点
   */
  constructor(firstNode: T) {
    firstNode !== undefined && this.append(firstNode)
  }

  /** 添加节点 */
  append(node: T) {
    const newNode = new LinkedNode(node)
    // 当前链表中一个元素都没有
    if (!this.length) {
      this.#head = this.#pointerNode = this.#tail = newNode
      this.length++
    }
    // #pointerNode 等于最后一个节点
    else if (this.#pointerNode === this.#tail) {
      this.#tail.next = newNode
      newNode.prev = this.#tail
      this.#tail = this.#pointerNode = newNode
      this.length++
    }
    // #pointerNode 不能最后一个节点，就要把 #pointerNode 之后的所有节点全部删除，再插入
    else {
      // 计算#pointerNode之后还有几个节点
      let nextNode = this.#pointerNode.next
      let size = 0
      while (nextNode) {
        size++
        nextNode = nextNode.next
      }
      // 获取被移除的节点，清除联系
      this.#pointerNode.next!.prev = null!
      // 重置 #tail 与 this.#pointerNode
      newNode.prev = this.#pointerNode
      this.#tail = this.#pointerNode.next = newNode
      this.#pointerNode = newNode
      // 重写长度
      this.length = this.length - size + 1
    }
  }

  /** 更新节点 */
  updatePointer(node: T) {
    if (!this.length) return
    const newNode = new LinkedNode(node)
    // 只有一个节点
    if (this.length === 1) {
      this.#head = this.#tail = this.#pointerNode = newNode
    }
    // 更新pointer节点数据
    else {
      // 如果当前节点是第一个节点
      if (this.#pointerNode === this.#head) {
        newNode.next = this.#head.next
        this.#head.next!.prev = newNode
        this.#head = this.#pointerNode = newNode
      }
      // 如果当前节点是最后一个节点
      else if (this.#pointerNode === this.#tail) {
        newNode.prev = this.#tail.prev
        this.#tail.prev!.next = newNode
        this.#tail = this.#pointerNode = newNode
      }
      // 中间节点
      else {
        const prevNode = this.#pointerNode.prev!
        const nextNode = this.#pointerNode.next!
        prevNode.next = this.#pointerNode = nextNode.prev = newNode
      }
    }
  }

  /** 移除开头节点 */
  removeHead() {
    if (this.length === 0) return
    if (this.#pointerNode === this.#head) {
      this.#pointerNode = this.#head.next!
      if (this.length === 1) this.#tail = null!
    }
    this.#head = this.#head.next!
    this.#head.prev = null
    this.length--
  }

  /** 是否存在上一个节点 */
  isPrev = () => this.#pointerNode !== this.#head

  /** 指向前一个节点 */
  prev = () => {
    if (!this.length) return false
    return this.#pointerNode = this.#pointerNode.prev!
  }

  /** 是否存在下一个节点 */
  isNext = () => {
    if (!this.length) return false
    return this.#pointerNode !== this.#tail
  }

  /** 指向下一个节点 */
  next = () => this.#pointerNode = this.#pointerNode.next!

  /** 获取当前指针 */
  getPointerNode = () => this.#pointerNode.item

}

export default HistoryStack